import { orgOrganizationsDecorationRetrieve } from "@innoclouds/api";
import { type DecorationApp, decorationForApp, isRecord } from "@innoclouds/ui";
import { useEventListener } from "@vueuse/core";
import { isAxiosError } from "axios";
import { computed, onScopeDispose, watch } from "vue";
import { useDecorationStore } from "#/store/decoration";
import { useOrgUnitStore } from "#/store/org-unit";
import { useThemeStore } from "#/store/theme";
import { applyDecorationPayload, clearDecorationPayload, reportDecorationError } from "#/utils/decorationAssets";
import { emitter } from "#/utils/emitter";

const LEGACY_SOURCE_KEY = "decoration_source";

export interface DecorationSyncOptions {
	precache?: boolean;
	app?: DecorationApp;
}

const isMissingDecoration = (error: unknown) => {
	if (!isAxiosError(error) || error.response?.status !== 404) return false;
	const body: unknown = error.response.data;
	return isRecord(body) && "detail" in body;
};

const isConnectivityError = (error: unknown) => isAxiosError(error) && (!error.response || error.response.status >= 500);

const handleSyncError = (error: unknown) => {
	if (isMissingDecoration(error)) clearDecorationPayload();
	else if (!isConnectivityError(error)) reportDecorationError(error, "decoration.sync");
};

const removeLegacySource = () => {
	try {
		localStorage.removeItem(LEGACY_SOURCE_KEY);
	} catch (error) {
		console.warn("[decoration] legacy source key not removed", error);
	}
};

export const useDecorationSync = ({ precache = true, app }: DecorationSyncOptions = {}) => {
	const orgUnitStore = useOrgUnitStore();
	const themeStore = useThemeStore();
	const decorationStore = useDecorationStore();

	const organizationId = computed(() => orgUnitStore.data?.organization ?? null);
	let latest = 0;
	let inFlight: string | null = null;
	let rerun = false;

	const fetchAndApply = async (id: string, isCurrent: () => boolean) => {
		try {
			const data: unknown = await orgOrganizationsDecorationRetrieve(id);
			if (!isCurrent() || !isRecord(data)) return;
			const decoration = decorationForApp(data, app);
			if (decoration) await applyDecorationPayload({ decoration }, { precache, isCurrent });
			else clearDecorationPayload();
		} catch (error) {
			if (isCurrent()) handleSyncError(error);
		}
	};

	const sync = async () => {
		const id = organizationId.value;
		if (!id || inFlight === id) return;
		const current = ++latest;
		const isCurrent = () => current === latest;
		inFlight = id;

		await fetchAndApply(id, isCurrent);
		decorationStore.refresh();
		if (!isCurrent()) return;

		inFlight = null;
		if (rerun) {
			rerun = false;
			void sync();
		}
	};

	const resync = () => {
		if (inFlight) rerun = true;
		void sync();
	};

	const onDecorationUpdate = (params: unknown) => {
		const hash = isRecord(params) ? params.hash_sum : undefined;
		if (!hash || hash !== decorationStore.data?.hash_sum) resync();
	};

	removeLegacySource();
	watch(organizationId, sync, { immediate: true });
	watch(() => themeStore.data, sync);
	useEventListener(window, "online", sync);
	if (precache && "serviceWorker" in navigator) useEventListener(navigator.serviceWorker, "controllerchange", resync);
	emitter.on("update_decoration", onDecorationUpdate);
	onScopeDispose(() => emitter.off("update_decoration", onDecorationUpdate));
};
